import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, EMPTY, of, switchMap } from 'rxjs';
import { Car, DEFAULT_SERVICES, ExtraService } from '../../manage-cars/manage-cars.interfaces';
import {
  CarImageUploadDialogComponent,
  CarImageUploadDialogData,
  CarImageUploadDialogResult,
} from '../car-image-upload-dialog/car-image-upload-dialog.component';
import { CarService } from '../../../services/car.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { mapAdminCarResponseToCar, mapCarToAdminCar } from '../../manage-cars/manage-cars.mappers';

export interface EditCarDialogData {
  modelId: string;
}

export interface EditCarDialogResult {
  success: boolean;
}

@Component({
  selector: 'app-edit-car-dialog',
  templateUrl: './edit-car-dialog.component.html',
  styleUrls: ['./edit-car-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditCarDialogComponent implements OnInit {
  loading = true;
  car: Car | null = null;

  editForm?: FormGroup;

  pendingImageFile: File | null = null;
  shouldDeleteExistingImage = false;
  hasExistingImage = false;
  displayImageIndicator = false;
  existingImageFileName: string | null = null;

  defaultServices: ExtraService[] = [];
  customServices: ExtraService[] = [];
  isAddingNewService = false;
  newServiceForm: FormGroup;

  seatOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  luggageOptions = [0, 1, 2, 3, 4, 5, 6];

  constructor(
    private dialogRef: MatDialogRef<EditCarDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditCarDialogData,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private carService: CarService,
    private snackbarService: SnackbarService,
    private cdr: ChangeDetectorRef,
  ) {
    this.newServiceForm = this.fb.group({
      nameHu: ['', Validators.required],
      nameEn: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.loadCarData();
  }

  private loadCarData(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.carService
      .getCarByKey$(this.data.modelId)
      .pipe(
        catchError(() => {
          this.snackbarService.error('A jármű adatainak lekérése során hiba lépett fel!');

          setTimeout(() => this.dialogRef.close({ success: false }), 1500);
          return of();
        }),
      )
      .subscribe((apiCar) => {
        if (apiCar) {
          this.car = mapAdminCarResponseToCar(apiCar);
          this.initializeForm(this.car);
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private initializeForm(car: Car): void {
    this.editForm = this.fb.group({
      name: [car.name, Validators.required],
      make: [car.make, Validators.required],
      model: [car.model, Validators.required],
      category: [car.category, Validators.required],
      modelId: [car.modelId, Validators.required],
      numberOfSeats: [car.numberOfSeats, Validators.required],
      largeLuggageCapacity: [car.largeLuggageCapacity, Validators.required],
      smallLuggageCapacity: [car.smallLuggageCapacity, Validators.required],
      transmission: [car.transmission, Validators.required],
    });

    this.initializeServices();

    this.hasExistingImage = !!car.imageAssetUrl;
    this.displayImageIndicator = this.hasExistingImage;
    this.shouldDeleteExistingImage = false;
    this.pendingImageFile = null;

    if (car.imageAssetUrl) {
      const urlParts = car.imageAssetUrl.split('/');
      this.existingImageFileName = urlParts[urlParts.length - 1] || 'image.jpg';
    }
  }

  initializeServices(): void {
    const carExtras = this.car?.extras || [];

    this.defaultServices = DEFAULT_SERVICES.map((defaultService) => {
      const existingService = carExtras.find((extra) => !extra.isCustom && extra.nameHu === defaultService.nameHu);
      return {
        ...defaultService,
        enabled: existingService ? existingService.enabled : true,
      };
    });

    this.customServices = carExtras.filter((extra) => extra.isCustom);
  }

  toggleDefaultService(index: number): void {
    this.defaultServices[index].enabled = !this.defaultServices[index].enabled;
  }

  toggleCustomService(index: number): void {
    this.customServices[index].enabled = !this.customServices[index].enabled;
  }

  showAddServiceForm(): void {
    this.isAddingNewService = true;
    this.newServiceForm.reset();
  }

  cancelAddService(): void {
    this.isAddingNewService = false;
    this.newServiceForm.reset();
  }

  addNewService(): void {
    if (this.newServiceForm.valid) {
      const newService: ExtraService = {
        nameHu: this.newServiceForm.value.nameHu,
        nameEn: this.newServiceForm.value.nameEn,
        enabled: true,
        isCustom: true,
      };
      this.customServices.push(newService);
      this.isAddingNewService = false;
      this.newServiceForm.reset();
    }
  }

  removeCustomService(index: number): void {
    this.customServices.splice(index, 1);
  }

  openImageUploadDialog(): void {
    const dialogRef = this.dialog.open(CarImageUploadDialogComponent, {
      data: {
        currentFileName: this.displayImageIndicator ? this.pendingImageFile?.name || this.existingImageFileName : null,
        modelId: this.data.modelId,
      } as CarImageUploadDialogData,
      width: '90%',
      maxWidth: '600px',
      panelClass: 'white-dialog',
    });

    dialogRef.afterClosed().subscribe((result: CarImageUploadDialogResult) => {
      if (result?.success) {
        if (result.deleted) {
          this.shouldDeleteExistingImage = true;
          this.pendingImageFile = null;
          this.displayImageIndicator = false;
        } else if (result.file) {
          this.pendingImageFile = result.file;
          this.shouldDeleteExistingImage = false;
          this.displayImageIndicator = true;
        }
        this.cdr.markForCheck();
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  softDeleteImage(): void {
    this.shouldDeleteExistingImage = true;
    this.pendingImageFile = null;
    this.displayImageIndicator = false;
    this.cdr.markForCheck();
  }

  onSave(): void {
    if (!this.editForm?.valid || !this.car) {
      return;
    }

    const formValue = this.editForm!.value;
    const allServices = [...this.defaultServices, ...this.customServices];

    const updatedCar: Car = {
      ...this.car,
      ...formValue,
      extras: allServices,
    };

    const adminCar = mapCarToAdminCar(updatedCar);
    const fileToUpload = this.pendingImageFile || undefined;

    const deleteAssetsFirst$ = fileToUpload
      ? this.carService.deleteCarAssetByKey$(this.data.modelId).pipe(
          catchError(() => {
            this.snackbarService.error('Hiba a kép törlése során!');
            return EMPTY;
          }),
        )
      : of(true);

    deleteAssetsFirst$
      .pipe(
        switchMap(() =>
          this.carService.updateCarByKey$(this.data.modelId, adminCar, fileToUpload).pipe(
            catchError(() => {
              this.snackbarService.error('Sikertelen módosítás. Kérjük, próbáld újra!');
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(() => {
        this.snackbarService.success('Sikeres módosítás');
        this.dialogRef.close({ success: true });
      });
  }
}
